import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  constructor(private readonly http: HttpClient) {}

  getCategoriesData(shopCode) {
    let params = new HttpParams().set('shopCode', shopCode);
    return this.http.get(`${environment.api_url}kpis/catalog`, { params });
  }

  toSunburstChart(values) {
    const data = this.toSunburstData(values);
    return {
      tooltip: {
        trigger: 'item',
      },
      series: {
        type: 'sunburst',
        data: data,
        radius: [60, '90%'],
        itemStyle: {
          borderRadius: 7,
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {},
      },
    };
  }

  private addOrMerge(children, item, level) {
    const nameKey = `level${level}Name`;
    const countKey = `level${level}Count`;
    const nextLevel = level + 1;

    if (!item[nameKey] || !item[countKey]) {
      return;
    }

    let child = children.find((c) => c.name === item[nameKey]);

    if (child) {
      child.value += item[countKey];
    } else {
      child = { name: item[nameKey], value: item[countKey], children: [] };
      children.push(child);
    }

    this.addOrMerge(child.children, item, nextLevel);
  }

  toSunburstData(dataArray) {
    const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666'];
    const chartData = [];

   for (let index = 0; index < dataArray.length; index++) {
     const item = dataArray[index];
     let parent = chartData.find((c) => c.name === item.level1Name);

     if (parent === undefined) {
       parent = {
         name: item.level1Name,
         value: item.level1Count,
         itemStyle: { color: colors[index % colors.length] },
         children: [],
       };
       chartData.push(parent);
     } else {
       parent.value += item.level1Count;
     }

     this.addOrMerge(parent.children, item, 2);
   }
    return chartData;
  }

  toBarChart() {
    const seriesData = this.getInitialSeriesData();
    const stackInfo = this.calculateStackInfo(seriesData);

    this.applyStyleToSeriesData(seriesData, stackInfo);

    return this.createChartConfiguration(seriesData);
  }

  private getInitialSeriesData() {
    return [
      {
        data: [120, 200, 150, 80, 70, 110, 130],
        type: 'bar',
        stack: 'a',
        name: 'a',
      },
      {
        data: [10, 46, 64, 25, 60, 70, 80],
        type: 'bar',
        stack: 'a',
        name: 'b',
      },
    ];
  }

  private calculateStackInfo(series) {
    const stackInfo: {
      [key: string]: { stackStart: number[]; stackEnd: number[] };
    } = {};

    for (let dataIndex = 0; dataIndex < series[0].data.length; ++dataIndex) {
      for (let seriesIndex = 0; seriesIndex < series.length; ++seriesIndex) {
        const stackName = series[seriesIndex].stack;
        if (!stackName) continue;

        const info = this.getOrCreateStackInfo(stackInfo, stackName);
        const data = series[seriesIndex].data[dataIndex];

        if (data) {
          info.stackStart[dataIndex] ??= seriesIndex;
          info.stackEnd[dataIndex] = seriesIndex;
        }
      }
    }

    return stackInfo;
  }

  private getOrCreateStackInfo(stackInfo, stackName) {
    stackInfo[stackName] ??= {
      stackStart: [],
      stackEnd: [],
    };
    return stackInfo[stackName];
  }

  private applyStyleToSeriesData(series, stackInfo) {
    for (let seriesIndex = 0; seriesIndex < series.length; ++seriesIndex) {
      const currentSeries = series[seriesIndex];
      const data = currentSeries.data as
        | number[]
        | { value: number; itemStyle: object }[];
      const info = stackInfo[currentSeries.stack];

      if (!info) continue;

      for (
        let dataIndex = 0;
        dataIndex < currentSeries.data.length;
        ++dataIndex
      ) {
        const isEnd = info.stackEnd[dataIndex] === seriesIndex;
        data[dataIndex] = this.createStyledDataPoint(
          data[dataIndex] as number,
          isEnd,
        );
      }
    }
  }

  private createStyledDataPoint(value: number, isEnd: boolean) {
    const topBorder = isEnd ? 10 : 0;
    const color = isEnd ? '#FFACA7' : '#C8E6C9';

    return {
      value: value,
      itemStyle: {
        color: color,
        borderRadius: [topBorder, topBorder, 0, 0],
      },
    };
  }

  private createChartConfiguration(series) {
    return {
      label: {
        show: true,
        fontStyle: 'bold',
      },
      xAxis: {
        type: 'category',
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      },
      yAxis: {
        type: 'value',
      },
      series: series,
    };
  }

  fake() {
    const data = {
      fileName: 'filename.csv',
    };
    return this.http.post(`${environment.api_url}storage/file`, data);
  }
}
